import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  type User,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { queryClient } from "@/lib/queryClient";
import { syncAdminAccess } from "@/lib/callables";

interface AuthState {
  user: User | null;
  /** Verdadeiro só quando o custom claim `admin` está presente no token. */
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (next) => {
      if (!next) {
        setUser(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }
      // A allowlist do projeto é quem manda, e ela é consultada A CADA entrada:
      // é assim que acrescentar um UID no Console concede o acesso sem mais
      // nada, e que tirar o UID de lá o revoga.
      //
      // A chamada vem ANTES da leitura do token, porque `setCustomUserClaims`
      // não altera o token já emitido: sem essa ordem, quem acabou de ser
      // incluído só entraria na segunda tentativa.
      //
      // Ela nunca derruba a entrada. Se a callable falhar (rede, ou o backend
      // ainda sem ela publicada), vale o que estiver no token, que é o
      // comportamento antigo.
      try {
        await syncAdminAccess();
      } catch (error) {
        console.warn("Não foi possível sincronizar o acesso de administrador:", error);
      }

      // `getIdTokenResult(true)` força a releitura do token. Sem o `true`, um
      // claim concedido agora só apareceria na próxima renovação (até uma hora
      // depois), e a pessoa recém-promovida veria "sem permissão".
      const token = await next.getIdTokenResult(true);
      setUser(next);
      setIsAdmin(token.claims.admin === true);
      setIsLoading(false);
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAdmin,
      isLoading,
      async signIn(email, password) {
        await signInWithEmailAndPassword(auth, email, password);
      },
      /**
       * Entrada com conta Google.
       *
       * Existe porque a conta de quem administra o projeto costuma ser uma
       * conta Google, e conta Google **não tem senha** no Firebase Auth: não
       * há o que digitar no formulário, e o Console também não permite
       * criá-la à mão.
       *
       * A sequência é: entrar com o Google, o que cria a conta no projeto;
       * o painel barra por falta do claim e mostra o UID; quem administra
       * copia esse UID para a allowlist; a pessoa entra de novo, e passa.
       *
       * `signInWithPopup` e não `signInWithRedirect`: o redirect depende de
       * cookie de terceiro, que os navegadores vêm restringindo, e este é um
       * painel de desktop, onde a janela suspensa é o caminho previsível.
       */
      async signInWithGoogle() {
        const provider = new GoogleAuthProvider();
        // Força a escolha da conta. Sem isso, quem tem sessão Google aberta
        // entra sempre com ela, e trocar de conta vira um quebra-cabeça.
        provider.setCustomParameters({ prompt: "select_account" });
        await signInWithPopup(auth, provider);
      },
      async signOut() {
        await firebaseSignOut(auth);
        // O cache guarda dado de gestão do projeto inteiro. Deixá-lo para a
        // próxima sessão mostraria número de um ambiente ou de uma conta que
        // não é mais a atual.
        queryClient.clear();
      },
      async resetPassword(email) {
        await sendPasswordResetEmail(auth, email);
      },
    }),
    [user, isAdmin, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de AuthProvider");
  return ctx;
}

/** Traduz o erro do Firebase Auth para o que o administrador precisa ler. */
export function describeAuthError(error: unknown): string {
  const code = (error as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/invalid-email":
      return "E-mail inválido.";
    case "auth/user-disabled":
      return "Esta conta está desativada.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "E-mail ou senha incorretos.";
    case "auth/too-many-requests":
      return "Muitas tentativas. Aguarde alguns minutos e tente de novo.";
    case "auth/network-request-failed":
      return "Sem conexão com o servidor.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "A janela do Google foi fechada antes de concluir.";
    case "auth/popup-blocked":
      return "O navegador bloqueou a janela do Google. Libere a janela suspensa para este site e tente de novo.";
    case "auth/account-exists-with-different-credential":
      return "Já existe uma conta com este e-mail, criada por outro método de entrada.";
    case "auth/unauthorized-domain":
      return "Este domínio não está autorizado no Firebase Auth. Acrescente-o em Authentication, Configurações, Domínios autorizados.";
    case "auth/operation-not-allowed":
      return "A entrada com Google não está habilitada neste projeto. Ligue-a em Authentication, Método de login.";
    default:
      return "Não foi possível entrar. Tente de novo.";
  }
}
